import { ChatMessage, BedrockMessage } from './chatTypes';
import { CHAT_CONFIG } from './chatConfig';

/**
 * Maps ChatMessage[] to BedrockMessage[] format for the Bedrock Converse API.
 *
 * - Filters out messages with status 'error' (failed messages excluded from history)
 * - Filters out messages with status 'streaming' (incomplete messages excluded)
 * - Converts remaining messages to Bedrock's expected { role, content: [{ text }] } format
 * - Maintains chronological order
 * - Ensures alternating user/assistant roles (valid Bedrock format)
 */
export function mapToBedrockMessages(messages: ChatMessage[]): BedrockMessage[] {
  // Filter out error and streaming messages
  const validMessages = messages.filter(
    (msg) => msg.status !== 'error' && msg.status !== 'streaming'
  );

  // Convert to Bedrock format while enforcing alternating roles
  const bedrockMessages: BedrockMessage[] = [];

  for (const msg of validMessages) {
    const lastMessage = bedrockMessages[bedrockMessages.length - 1];

    // Skip if same role as previous (Bedrock requires alternating roles)
    if (lastMessage && lastMessage.role === msg.role) {
      continue;
    }

    bedrockMessages.push({
      role: msg.role,
      content: [{ text: msg.content }],
    });
  }

  return bedrockMessages;
}

/**
 * Truncates conversation history to the most recent N message pairs.
 * A "pair" is one user message followed by one assistant message.
 *
 * - Limits to maxHistoryPairs pairs (default 50, configurable via CHAT_CONFIG)
 * - Drops oldest pairs first when exceeding limit
 * - If the last message is a lone user message (no assistant reply yet), it's preserved
 */
export function truncateHistory(messages: BedrockMessage[]): BedrockMessage[] {
  const maxPairs = CHAT_CONFIG.maxHistoryPairs;
  const maxMessages = maxPairs * 2;

  if (messages.length <= maxMessages) {
    return messages;
  }

  // Keep the most recent messages up to the limit
  return messages.slice(messages.length - maxMessages);
}

/**
 * Prepares conversation history for a Bedrock API request.
 * Combines filtering, mapping, and truncation in one call.
 *
 * - Excludes error/streaming messages
 * - Ensures alternating roles
 * - Truncates to most recent 50 pairs
 */
export function prepareConversationHistory(messages: ChatMessage[]): BedrockMessage[] {
  const mapped = mapToBedrockMessages(messages);
  return truncateHistory(mapped);
}
